import type { Profile } from './models';

const normalizeSkill = (s: string) =>
  s
    .toLowerCase()
    .replace(/back[- ]end/g, 'backend')
    .replace(/front[- ]end/g, 'frontend')
    .replace(/engineering/g, 'engineer')
    .replace(/development/g, 'developer')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
const skillFamilies: Array<[string, string, RegExp]> = [
  ['backend', 'software', /\b(backend|api|python|fastapi|django|postgresql|database|node js)\b/],
  ['frontend', 'software', /\b(frontend|react|vue|angular|javascript|html|css)\b/],
  ['mobile', 'software', /\b(mobile|android|ios|flutter|swift|kotlin)\b/],
  ['infrastructure', 'software', /\b(devops|cloud|infrastructure|deployment|kubernetes|docker)\b/],
  ['data', 'software', /\b(data science|analytics|machine learning|ml|ai|data engineer)\b/],
  ['software', 'software', /\b(engineer|software|coding|developer|programming|full stack)\b/],
  ['design', 'design', /\b(design|designer|ux|ui|branding|graphic)\b/],
  [
    'marketing',
    'marketing',
    /\b(marketing|seo|copywriting|copywriter|campaign|content|social media|public relations)\b/,
  ],
  ['sales', 'sales', /\b(sales|selling|business developer|customer acquisition)\b/],
  ['product', 'product', /\b(product|roadmap|user research)\b/],
  ['finance', 'finance', /\b(finance|financial|accounting|accountant|bookkeeping|tax)\b/],
  ['legal', 'legal', /\b(legal|compliance|privacy|contracts|law)\b/],
  [
    'investment',
    'investment',
    /\b(investor|investing|investment|fundraising|venture capital|vc)\b/,
  ],
  ['operations', 'operations', /\b(operations|logistics|supply chain|manufacturing)\b/],
  ['people', 'people', /\b(human resources|hr|recruiting|recruitment|people management)\b/],
  ['strategy', 'strategy', /\b(strategy|strategist|business planning)\b/],
];
export function skillAffinity(need: string, offer: string) {
  const n = normalizeSkill(need),
    o = normalizeSkill(offer);
  if (!n || !o) return 0;
  if (n === o) return 1;
  const a = skillFamilies.find(([, , pattern]) => pattern.test(n));
  const b = skillFamilies.find(([, , pattern]) => pattern.test(o));
  if (a && b) return a[0] === b[0] ? 0.8 : a[1] === b[1] ? 0.45 : 0;
  const words = n.split(' ').filter((w) => w.length >= 4);
  return words.length && words.every((w) => o.split(' ').includes(w)) ? 0.7 : 0;
}
export function discoveryRelevance(viewer: Profile, candidate: Profile) {
  const needs = [
    ...new Set([...viewer.seeking, ...viewer.wants].map(normalizeSkill).filter(Boolean)),
  ];
  const offers = [
    ...candidate.skills,
    ...candidate.brings,
    ...(candidate.ai?.specialisms ?? []),
    candidate.ai?.profession ?? '',
  ];
  return needs.length
    ? needs.reduce(
        (total, need) => total + Math.max(0, ...offers.map((offer) => skillAffinity(need, offer))),
        0,
      ) / needs.length
    : 0;
}
// Needs coverage takes precedence; working-style compatibility breaks ties.
export function compareDiscovery(viewer: Profile, a: Profile, b: Profile) {
  return (
    discoveryRelevance(viewer, b) - discoveryRelevance(viewer, a) ||
    matchProfiles(viewer, b).score - matchProfiles(viewer, a).score ||
    a.id.localeCompare(b.id)
  );
}
export function matchProfiles(a: Profile, b: Profile) {
  const complementaryStrengths = b.skills.filter((x) => a.seeking.includes(x));
  const reciprocal = a.skills.filter((x) => b.seeking.includes(x));
  const common = a.industries.filter((x) => b.industries.includes(x));
  const reasons: string[] = [];
  const mismatches: string[] = [];
  const coverage = (have: string[], want: string[]) =>
    want.length ? have.length / want.length : 0;
  let score =
    35 * coverage(complementaryStrengths, a.seeking) + 25 * coverage(reciprocal, b.seeking);
  if (complementaryStrengths.length)
    reasons.push(
      `You’re looking for ${complementaryStrengths.join(' and ').toLowerCase()}; ${b.name.split(' ')[0]} brings it.`,
    );
  if (reciprocal.length)
    reasons.push(
      `Your ${reciprocal.join(' and ').toLowerCase()} experience fills a gap in their team.`,
    );
  if (common.length) {
    score += 12;
    reasons.push(`A shared interest in ${common.join(' and ').toLowerCase()}.`);
  }
  if (a.stage === b.stage) score += 6;
  else mismatches.push(`Different preferred stages: ${a.stage} and ${b.stage}.`);
  const hoursRatio = Math.min(a.hours, b.hours) / Math.max(a.hours, b.hours);
  score += 8 * hoursRatio;
  if (hoursRatio < 0.5)
    mismatches.push(`Different time budgets: ${a.hours} vs ${b.hours} hours a week.`);
  if (a.style === b.style) score += 5;
  else mismatches.push('Discuss how often you want to check in.');
  const locationCompatible =
    !(a.preference === 'Local' || b.preference === 'Local') ||
    a.location.toLowerCase() === b.location.toLowerCase();
  if (locationCompatible) score += 5;
  else mismatches.push('Local collaboration preferences do not align.');
  score += 4 * Math.max(0, 1 - Math.abs(a.teamSize - b.teamSize) / 5);
  if (!a.looking || !b.looking) {
    score = 0;
    mismatches.push('One of you is not currently looking for collaborators.');
  }
  if (!locationCompatible) score = Math.min(score, 45);
  if (!reasons.length)
    reasons.push('Explore your working styles and goals before making a commitment.');
  return {
    score: Math.round(Math.max(0, Math.min(100, score))),
    reasons,
    mismatches,
    complementaryStrengths,
  };
}
